// mc-delivery.js, Producer D, audit-2026-09-18
// Wires every [data-mc-delivery] block on the page (there can be more than one: the product page
// buy box, and the cart assurance section). Two modes, set via data-mc-mode on the block:
//   product - looks up the static table in window.MC_ZONES (built by build-zones.mjs from the
//             Shipeasy probe log). Always available, never blocks on a network call.
//   cart    - calls the live storefront endpoint for the actual cart contents, and falls back to the
//             static table (marked estimated) on a 429 or a timeout.
// Depends on assets/mc-delivery-zones.js having run first (window.MC_ZONES, window.MC_STATE_NAMES)
// and, for the cutoff-line phone number, window.MC_FACTS from assets/mc-facts.js.
(function () {
  'use strict';

  var STORAGE_KEY = 'mc_postcode';

  function readStoredPostcode() {
    try { return window.localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }
  function storePostcode(pc) {
    try { window.localStorage.setItem(STORAGE_KEY, pc); } catch (e) { /* private window or blocked storage, ignore */ }
  }

  function phone() {
    return (window.MC_FACTS && window.MC_FACTS.phone) || '0408 106 552';
  }
  function deliveryWindow() {
    return (window.MC_FACTS && window.MC_FACTS.deliveryWindow) || '2 to 12';
  }

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-AU');
  }

  function suburbLabel(zone, postcode) {
    if (zone && zone.loc) return zone.loc.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); }) + ' ' + postcode;
    return postcode;
  }

  function toDollars(price) {
    var n = typeof price === 'number' ? price : parseFloat(price);
    return isFinite(n) ? n : null;
  }

  // Same classification rules as build-zones.mjs, applied to a live /cart/shipping_rates.json response.
  function classifyRates(rates) {
    if (!rates || !rates.length) return null;
    var i;
    for (i = 0; i < rates.length; i++) {
      if (/next[\s-]?day/i.test(rates[i].name || '')) return { type: 'metro-nextday' };
    }
    for (i = 0; i < rates.length; i++) {
      if (/free/i.test(rates[i].name || '')) return { type: 'free' };
    }
    for (i = 0; i < rates.length; i++) {
      if (/fee/i.test(rates[i].name || '')) {
        var p = toDollars(rates[i].price);
        if (p !== null && p > 0) return { type: 'fee', fee: p };
      }
    }
    if (rates.length === 1) {
      var p2 = toDollars(rates[0].price);
      if (p2 !== null) return p2 > 0 ? { type: 'fee', fee: p2 } : { type: 'free' };
    }
    return null;
  }

  function resultMarkup(type, fee, suburb, postcode, estimated) {
    var estNote = estimated ? '<span class="mcbt-est">Based on deliveries near ' + postcode + '. The exact cost for your address shows in the cart before you pay.</span>' : '';
    if (type === 'metro-nextday') {
      return '<span>' + (estimated ? 'Deliveries to <b>' + suburb + '</b> are usually free and next business day.' : 'Free next business day delivery to <b>' + suburb + '</b>. Order before the cutoff and it is on the truck today.') + '</span>' + estNote;
    }
    if (type === 'free') {
      return '<span>' + (estimated ? 'Deliveries to <b>' + suburb + '</b> are usually free, ' : 'Free delivery to <b>' + suburb + '</b>, usually ') + deliveryWindow() + ' business days from dispatch.</span>' + estNote;
    }
    if (type === 'fee') {
      return '<span>' + (estimated ? 'Deliveries to <b>' + suburb + '</b> usually carry a fee of about <b>' + money(fee) + '</b>.' : 'Delivery to <b>' + suburb + '</b> is <b>' + money(fee) + '</b>.') + ' Free delivery covers the metro areas our courier partner runs daily. Past that we pay the courier by the kilometre, and on our margins that is more than the mattress makes, so we pass on the exact cost rather than hiding it in the price for everyone else.</span>' +
        '<span class="mcbt-rural">As a family from rural Australia ourselves we know how that lands. If there is a freight depot or a rural hub in your area, delivery to the hub for you to collect is often much cheaper. Call us on ' + phone() + ' and we will price it.</span>' + estNote;
    }
    return '<span>We could not price ' + postcode + ' automatically. Call us on ' + phone() + ' or email info@mattresscrafters.com.au and we will quote it the same day.</span>';
  }

  function staticLookup(postcode) {
    var zone = window.MC_ZONES && window.MC_ZONES[postcode];
    if (!zone) return { type: 'none', estimated: true };
    return { type: zone.type, fee: zone.fee, estimated: zone.confidence === 'estimated', zone: zone };
  }

  function showResult(block, html, state) {
    var result = block.querySelector('[data-mc-result]');
    if (!result) return;
    result.innerHTML = html;
    result.hidden = false;
    if (state) result.setAttribute('data-state', state); else result.removeAttribute('data-state');
  }

  function runProductMode(block, postcode) {
    showResult(block, 'Checking delivery to ' + postcode + '…', 'loading');
    var r = staticLookup(postcode);
    var suburb = suburbLabel(r.zone, postcode);
    showResult(block, resultMarkup(r.type, r.fee, suburb, postcode, r.estimated));
  }

  function stateNameFor(postcode) {
    var zone = window.MC_ZONES && window.MC_ZONES[postcode];
    var st = zone && zone.st;
    if (!st || !window.MC_STATE_NAMES) return '';
    return window.MC_STATE_NAMES[st] || '';
  }

  function fetchJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      return { status: res.status, body: res.status === 200 || res.status === 202 ? res.json().catch(function () { return null; }) : Promise.resolve(null) };
    }).then(function (r) { return Promise.resolve(r.body).then(function (body) { return { status: r.status, body: body }; }); });
  }

  function runCartMode(block, postcode) {
    showResult(block, 'Checking delivery to ' + postcode + '…', 'loading');
    var province = stateNameFor(postcode);
    var qs = 'shipping_address[zip]=' + encodeURIComponent(postcode) + '&shipping_address[country]=Australia' + (province ? '&shipping_address[province]=' + encodeURIComponent(province) : '');

    function fallback() {
      var r = staticLookup(postcode);
      var suburb = suburbLabel(r.zone, postcode);
      showResult(block, resultMarkup(r.type, r.fee, suburb, postcode, true));
    }

    function pollAsync(elapsed) {
      if (elapsed >= 20000) { fallback(); return; }
      fetchJson('/cart/async_shipping_rates.json?' + qs).then(function (r) {
        if (r.status === 200 && r.body && r.body.shipping_rates) {
          handleRates(r.body.shipping_rates);
        } else if (r.status === 429) {
          fallback();
        } else {
          setTimeout(function () { pollAsync(elapsed + 2000); }, 2000);
        }
      }).catch(fallback);
    }

    function handleRates(rates) {
      var c = classifyRates(rates);
      var zone = window.MC_ZONES && window.MC_ZONES[postcode];
      var suburb = suburbLabel(zone, postcode);
      if (!c) { showResult(block, resultMarkup('none', null, suburb, postcode, false)); return; }
      showResult(block, resultMarkup(c.type, c.fee, suburb, postcode, false));
    }

    fetchJson('/cart/shipping_rates.json?' + qs).then(function (r) {
      if (r.status === 200 && r.body && r.body.shipping_rates) {
        handleRates(r.body.shipping_rates);
      } else if (r.status === 202) {
        setTimeout(function () { pollAsync(2000); }, 2000);
      } else if (r.status === 429) {
        fallback();
      } else {
        fallback();
      }
    }).catch(fallback);
  }

  function check(block) {
    var input = block.querySelector('[data-mc-postcode]');
    if (!input) return;
    var postcode = (input.value || '').trim();
    if (!/^\d{4}$/.test(postcode)) {
      showResult(block, 'Enter a 4 digit postcode.', 'error');
      return;
    }
    storePostcode(postcode);
    // keep every instance on the page in sync
    document.querySelectorAll('[data-mc-postcode]').forEach(function (i) { i.value = postcode; });
    var mode = block.getAttribute('data-mc-mode') || 'product';
    if (mode === 'cart') runCartMode(block, postcode); else runProductMode(block, postcode);
  }

  function wire(block) {
    if (block.getAttribute('data-mc-wired')) return;
    block.setAttribute('data-mc-wired', '1');
    var input = block.querySelector('[data-mc-postcode]');
    var btn = block.querySelector('[data-mc-check]');
    if (input) {
      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '').slice(0, 4);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); check(block); }
      });
      var stored = readStoredPostcode();
      if (stored) input.value = stored;
    }
    if (btn) btn.addEventListener('click', function () { check(block); });
  }

  function wireAll() {
    var blocks = document.querySelectorAll('[data-mc-delivery]');
    for (var i = 0; i < blocks.length; i++) wire(blocks[i]);
  }

  function init() {
    wireAll();
    var app = document.getElementById('app');
    if (app && window.MutationObserver) {
      var mo = new MutationObserver(wireAll);
      mo.observe(app, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
